// Game logic utilities for JustFun website
// Reused from Android app logic

export interface Tile {
  number: number | null;
  position: number;
}

export interface SudokuCell {
  value: number | null;
  isOriginal: boolean;
  isError: boolean;
}

export interface TicTacToeCell {
  value: 'X' | 'O' | null;
  position: number;
}

// 8-Puzzle Game Logic
export class Puzzle8Game {
  private tiles: Tile[] = [];
  private moves: number = 0;
  private isWon: boolean = false;
  private startTime: number = 0;

  constructor() {
    this.initializeGame();
  }

  initializeGame() {
    // Create solved state first, then shuffle
    this.tiles = [
      { number: 1, position: 0 },
      { number: 2, position: 1 },
      { number: 3, position: 2 },
      { number: 4, position: 3 },
      { number: 5, position: 4 },
      { number: 6, position: 5 },
      { number: 7, position: 6 },
      { number: 8, position: 7 },
      { number: null, position: 8 } // Empty tile
    ];
    
    this.shuffleTiles();
    this.moves = 0;
    this.isWon = false;
    this.startTime = Date.now();
  }

  private shuffleTiles() {
    // Shuffle by making random valid moves
    for (let i = 0; i < 1000; i++) {
      const emptyIndex = this.tiles.findIndex(tile => tile.number === null);
      const adjacentTiles = this.getAdjacentTiles(emptyIndex);
      if (adjacentTiles.length > 0) {
        const randomTile = adjacentTiles[Math.floor(Math.random() * adjacentTiles.length)];
        this.swapTiles(randomTile, emptyIndex);
      }
    }
  }

  private getAdjacentTiles(emptyIndex: number): number[] {
    const adjacent: number[] = [];
    const row = Math.floor(emptyIndex / 3);
    const col = emptyIndex % 3;

    // Check all 4 directions
    if (row > 0) adjacent.push(emptyIndex - 3); // Up
    if (row < 2) adjacent.push(emptyIndex + 3); // Down
    if (col > 0) adjacent.push(emptyIndex - 1); // Left
    if (col < 2) adjacent.push(emptyIndex + 1); // Right

    return adjacent;
  }

  onTileClick(index: number): boolean {
    if (this.isWon) return false;

    const emptyIndex = this.tiles.findIndex(tile => tile.number === null);
    const canMove = this.isAdjacent(index, emptyIndex);

    if (canMove) {
      this.swapTiles(index, emptyIndex);
      this.moves++;
      this.checkWinCondition();
      return true;
    }
    return false;
  }

  private swapTiles(index1: number, index2: number) {
    const temp = this.tiles[index1];
    this.tiles[index1] = this.tiles[index2];
    this.tiles[index2] = temp;
  }

  private isAdjacent(index1: number, index2: number): boolean {
    const row1 = Math.floor(index1 / 3);
    const col1 = index1 % 3;
    const row2 = Math.floor(index2 / 3);
    const col2 = index2 % 3;
    
    return Math.abs(row1 - row2) + Math.abs(col1 - col2) === 1;
  }

  private checkWinCondition() {
    this.isWon = this.tiles.every((tile, index) => 
      tile.number === null || tile.number === index + 1
    );
  }

  getTiles(): Tile[] { return [...this.tiles]; }
  getMoves(): number { return this.moves; }
  getIsWon(): boolean { return this.isWon; }
  getTimeElapsed(): number { return Date.now() - this.startTime; }
}

// Sudoku Game Logic
export class SudokuGame {
  private board: SudokuCell[][] = [];
  private difficulty: 'easy' | 'medium' | 'hard' = 'easy';
  private isWon: boolean = false;
  private startTime: number = 0;

  constructor(difficulty: 'easy' | 'medium' | 'hard' = 'easy') {
    this.difficulty = difficulty;
    this.initializeGame();
  }

  initializeGame() {
    this.board = Array(9).fill(null).map(() => 
      Array(9).fill(null).map(() => ({
        value: null,
        isOriginal: false,
        isError: false
      }))
    );
    
    this.generatePuzzle();
    this.isWon = false;
    this.startTime = Date.now();
  }

  private generatePuzzle() {
    // Generate a solved Sudoku
    this.solveSudoku();
    
    // Remove cells based on difficulty
    const cellsToRemove = this.difficulty === 'easy' ? 40 : 
                         this.difficulty === 'medium' ? 50 : 60;
    
    let removed = 0;
    while (removed < cellsToRemove) {
      const row = Math.floor(Math.random() * 9);
      const col = Math.floor(Math.random() * 9);
      
      if (this.board[row][col].value !== null) {
        this.board[row][col].value = null;
        this.board[row][col].isOriginal = false;
        removed++;
      }
    }
    
    // Mark remaining cells as original
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (this.board[row][col].value !== null) {
          this.board[row][col].isOriginal = true;
        }
      }
    }
  }

  private solveSudoku(): boolean {
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (this.board[row][col].value === null) {
          for (let num = 1; num <= 9; num++) {
            if (this.isValidMove(row, col, num)) {
              this.board[row][col].value = num;
              if (this.solveSudoku()) {
                return true;
              }
              this.board[row][col].value = null;
            }
          }
          return false;
        }
      }
    }
    return true;
  }

  onCellClick(row: number, col: number, value: number) {
    if (this.board[row][col].isOriginal) return false;
    
    this.board[row][col].value = value;
    this.checkErrors();
    this.checkWinCondition();
    return true;
  }

  private isValidMove(row: number, col: number, num: number): boolean {
    // Check row
    for (let x = 0; x < 9; x++) {
      if (this.board[row][x].value === num) return false;
    }
    
    // Check column
    for (let x = 0; x < 9; x++) {
      if (this.board[x][col].value === num) return false;
    }
    
    // Check 3x3 box
    const startRow = row - row % 3;
    const startCol = col - col % 3;
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        if (this.board[i + startRow][j + startCol].value === num) return false;
      }
    }
    
    return true;
  }

  private checkErrors() {
    // Reset all errors
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        this.board[row][col].isError = false;
      }
    }
    
    // Check for errors
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (this.board[row][col].value !== null) {
          if (!this.isValidMove(row, col, this.board[row][col].value!)) {
            this.board[row][col].isError = true;
          }
        }
      }
    }
  }

  private checkWinCondition() {
    this.isWon = this.board.every(row => 
      row.every(cell => cell.value !== null && !cell.isError)
    );
  }

  getBoard(): SudokuCell[][] { return this.board.map(row => [...row]); }
  getIsWon(): boolean { return this.isWon; }
  getTimeElapsed(): number { return Date.now() - this.startTime; }
}

// Tic-Tac-Toe Game Logic
export class TicTacToeGame {
  private board: TicTacToeCell[] = [];
  private currentPlayer: 'X' | 'O' = 'X';
  private winner: 'X' | 'O' | 'draw' | null = null;
  private gameOver: boolean = false;

  constructor() {
    this.initializeGame();
  }

  initializeGame() {
    this.board = Array(9).fill(null).map((_, index) => ({
      value: null,
      position: index
    }));
    this.currentPlayer = 'X';
    this.winner = null;
    this.gameOver = false;
  }

  onCellClick(index: number): boolean {
    if (this.gameOver || this.board[index].value !== null) return false;
    
    this.board[index].value = this.currentPlayer;
    this.checkWinCondition();
    
    if (!this.gameOver) {
      this.currentPlayer = this.currentPlayer === 'X' ? 'O' : 'X';
    }
    
    return true;
  }

  private checkWinCondition() {
    const winPatterns = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // Rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // Columns
      [0, 4, 8], [2, 4, 6] // Diagonals
    ];

    for (const pattern of winPatterns) {
      const [a, b, c] = pattern;
      if (this.board[a].value && 
          this.board[a].value === this.board[b].value && 
          this.board[a].value === this.board[c].value) {
        this.winner = this.board[a].value;
        this.gameOver = true;
        return;
      }
    }

    // Check for draw
    if (this.board.every(cell => cell.value !== null)) {
      this.winner = 'draw';
      this.gameOver = true;
    }
  }

  getBoard(): TicTacToeCell[] { return [...this.board]; }
  getCurrentPlayer(): 'X' | 'O' { return this.currentPlayer; }
  getWinner(): 'X' | 'O' | 'draw' | null { return this.winner; }
  getGameOver(): boolean { return this.gameOver; }
}

// Utility functions
export const formatTime = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const generateQRCode = (text: string): string => {
  // Simple QR code generation for Android app download
  // In a real app, you'd use a proper QR code library
  return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(text)}`;
};
